import { useState } from 'react';
import type { Machine, ShopUserResponse, UserRole } from '@bridgewerk/shared';
import { useBulkReplaceAssignments, useMachineAssignments } from '../hooks/useTeam.js';

const ROLE_BADGE: Record<UserRole, string> = {
  manager:    'text-accent',
  programmer: 'text-yellow',
  operator:   'text-green',
};

export interface AssignmentMatrixProps {
  readonly shopId: string;
  readonly machines: Machine[];
  readonly users: ShopUserResponse[];
}

export function AssignmentMatrix({ shopId, machines, users }: AssignmentMatrixProps): JSX.Element {
  const [selectedMachine, setSelectedMachine] = useState<string | null>(
    machines[0]?.machine_id ?? null,
  );

  const assignableUsers = users.filter(u => u.role !== 'manager' && u.is_active);

  return (
    <div className="space-y-3">
      {machines.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          {machines.map(m => (
            <button
              key={m.machine_id}
              type="button"
              onClick={() => setSelectedMachine(m.machine_id)}
              className={[
                'px-2.5 py-1 rounded-md text-xs font-mono transition-colors border',
                selectedMachine === m.machine_id
                  ? 'border-accent text-accent bg-accent/10'
                  : 'border-border text-text-dim hover:border-accent/50 hover:text-text',
              ].join(' ')}
            >
              {m.machine_id}
            </button>
          ))}
        </div>
      )}

      {selectedMachine ? (
        <MachineAssignmentPanel
          shopId={shopId}
          machineId={selectedMachine}
          users={assignableUsers}
        />
      ) : (
        <p className="text-xs font-mono text-muted py-4 text-center">No machines registered.</p>
      )}
    </div>
  );
}

interface MachineAssignmentPanelProps {
  readonly shopId: string;
  readonly machineId: string;
  readonly users: ShopUserResponse[];
}

function MachineAssignmentPanel({ shopId, machineId, users }: MachineAssignmentPanelProps): JSX.Element {
  const { data, isLoading } = useMachineAssignments(shopId, machineId);
  const bulkReplace = useBulkReplaceAssignments(shopId, machineId);

  // Collect current assignment user IDs and their role_context
  const existingMap = new Map<string, UserRole>();
  if (data) {
    for (const a of [...data.assigned_users.operators, ...data.assigned_users.programmers]) {
      existingMap.set(a.user_id, a.role_context);
    }
  }

  const [pending, setPending] = useState<Map<string, UserRole | null>>(() => new Map());

  const effectiveRole = (userId: string): UserRole | null => {
    if (pending.has(userId)) return pending.get(userId) ?? null;
    return existingMap.get(userId) ?? null;
  };

  const toggleAssignment = (userId: string, userRole: UserRole) => {
    const current = effectiveRole(userId);
    const next = new Map(pending);
    if (current === null) {
      // Assign with user's natural role_context
      next.set(userId, userRole === 'manager' ? 'operator' : userRole);
    } else {
      // Remove
      next.set(userId, null);
    }
    setPending(next);
  };

  const hasPending = pending.size > 0;

  const save = async () => {
    // Merge existingMap with pending overrides
    const merged = new Map(existingMap);
    for (const [uid, role] of pending.entries()) {
      if (role === null) {
        merged.delete(uid);
      } else {
        merged.set(uid, role);
      }
    }
    await bulkReplace.mutateAsync(
      [...merged.entries()].map(([userId, roleContext]) => ({
        user_id: userId,
        role_context: roleContext,
        is_primary: false,
        shift: null,
      })),
    );
    setPending(new Map());
  };

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-10 rounded-md bg-card/50 animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <p className="text-xs font-mono text-muted py-4 text-center">
        No operators or programmers on the team yet.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {users.map(u => {
        const assigned = effectiveRole(u.user_id);
        const isPendingChange = pending.has(u.user_id);
        return (
          <div
            key={u.user_id}
            className={[
              'flex items-center justify-between px-3 py-2.5 rounded-md border transition-colors',
              isPendingChange ? 'border-accent/40 bg-accent/5' : 'border-border bg-card/30',
            ].join(' ')}
          >
            <div className="min-w-0">
              <p className="text-sm font-mono text-text truncate">
                {u.display_name ?? u.email}
              </p>
              <p className={`text-[10px] font-mono capitalize ${ROLE_BADGE[u.role]}`}>
                {u.role}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleAssignment(u.user_id, u.role)}
              className={[
                'ml-3 px-2.5 py-1 rounded border text-[11px] font-mono transition-colors',
                assigned
                  ? 'border-accent text-accent bg-accent/10 hover:bg-red/10 hover:border-red hover:text-red'
                  : 'border-border text-text-dim hover:border-accent hover:text-accent',
              ].join(' ')}
            >
              {assigned ? 'Assigned ✓' : 'Assign'}
            </button>
          </div>
        );
      })}

      {hasPending && (
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => void save()}
            disabled={bulkReplace.isPending}
            className="flex-1 py-2 rounded-md bg-accent/15 border border-accent/30 text-sm font-mono text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
          >
            {bulkReplace.isPending ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={() => setPending(new Map())}
            className="px-4 py-2 rounded-md border border-border text-sm font-mono text-text-dim hover:text-text transition-colors"
          >
            Discard
          </button>
        </div>
      )}
    </div>
  );
}
